// libs
import isEmpty from 'lodash/isEmpty';

// helpers
import { ApiHelper } from 'shared/helpers/api';
import { getUrl, ROUTES } from 'shared/api/urls';
import { detectOS } from 'server/helpers/deviceUtility';

// defs
import { IHeadersObject } from 'global';
import { PINNED_PROD_IDS_SEARCHTERM } from 'shared/store/listing/helpers';
import { DEFAULT_COUNTRY, DEFAULT_CURRENCY_CODE } from 'shared/constants';

// constant
import { DEVICE_OS_KEY, DEVICE_OS_VALUE, MWEB_NEW_RANKING, MWEB_VECTOR_SEARCH, MWEB_RELATED_SEARCH } from 'shared/constants/listing';
const IS_FILTERED_URL = 'is_filtered_url';

interface IGetPLPData {
  categoryId?: string;
  searchTerm: string;
  headers?: IHeadersObject;
  fetchMeta?: boolean;
  filters?: Listing.IAppliedFilters;
  page?: number;
  onlyFilters?: boolean;
  pinnedProductsIds?: string;
  isFilteredUrl?: boolean;
  priceRevealEnabled?: boolean;
  isOffer?: boolean;
  isCouponOffer?: boolean;
  deviceOS?: string;
  catalogTagFilter?: string;
  vectorSearch?: {
    isEligible: boolean;
    variant: string;
    vectorSearchAlgo: any;
  };
  newUserRanking?: {
    isEligible: boolean;
    variant: string;
    userRankingAlgo: any;
  };
  relatedSearch?: {
    isEligible: boolean;
    variant: string;
  };
}

export const getPLPData = async ({
  categoryId,
  searchTerm,
  headers = {},
  page = 1,
  filters = {},
  onlyFilters = false,
  fetchMeta = false,
  pinnedProductsIds,
  isFilteredUrl = false,
  priceRevealEnabled = false,
  isOffer = false,
  isCouponOffer = false,
  deviceOS = '',
  catalogTagFilter,
  vectorSearch = { isEligible: false, variant: '', vectorSearchAlgo: {} },
  newUserRanking = { isEligible: false, variant: '', userRankingAlgo: {} },
  relatedSearch = { isEligible: false, variant: '' },
}: IGetPLPData): Promise<any> => {
  const searchParams = new URLSearchParams('');

  const { isEligible: isRelatedSearchEnabled = false, variant: isRelatedSearchVariant = '' } = relatedSearch;
  const { isEligible: isVectorSearchEnabled = false, variant: isVectorSearchVariant = '', vectorSearchAlgo = {} } = vectorSearch;
  const { isEligible: isNewRankingEnabled = false, variant: isNewRankingVariant = '', userRankingAlgo = {} } = newUserRanking;

  if (isVectorSearchEnabled && isVectorSearchVariant === MWEB_VECTOR_SEARCH.VARIANT_A) {
    const entries = Object.entries(vectorSearchAlgo || {});
    if (entries?.length > 0) {
      entries.forEach(([key, val]) => {
        if (val != null) {
          searchParams.append(key, String(val));
        }
      });
    } else {
      searchParams.append('vector_search_algo', vectorSearchAlgo?.vector_search_algo);
      searchParams.append('classifier_algo', vectorSearchAlgo?.classifier_algo);
    }
  }

  if (isNewRankingEnabled && isNewRankingVariant === MWEB_NEW_RANKING.VARIANT_A) {
    const entries = Object.entries(userRankingAlgo || {});
    if (entries?.length > 0) {
      entries.forEach(([key, val]) => {
        if (val != null) {
          searchParams.append(key, String(val));
        }
      });
    } else {
      searchParams.append('sort_algo', userRankingAlgo?.sort_algo);
    }
  }

  const isRelatedSearchUiVariant =
    isRelatedSearchVariant === MWEB_RELATED_SEARCH.VARIANT_A ||
    isRelatedSearchVariant === MWEB_RELATED_SEARCH.VARIANT_B;

  if (isRelatedSearchEnabled && isRelatedSearchUiVariant) {
    searchParams.append('enable_related_search_ui', '1');
    searchParams.append('apiVersion', '8');
  } else {
    searchParams.append('apiVersion', '6');
  }

  if (categoryId) {
    const paramName = isCouponOffer ? 'coupon_offer_id' : isOffer ? 'offer_id' : 'categoryId';
    searchParams.append(paramName, categoryId);
  }

  if (searchTerm) {
    searchParams.append('searchTerm', searchTerm);
  }

  if (page === 1 && pinnedProductsIds) {
    searchParams.append(PINNED_PROD_IDS_SEARCHTERM, pinnedProductsIds);
  }

  if (isFilteredUrl) {
    searchParams.append(IS_FILTERED_URL, 'true');
  }

  Object.keys(filters).forEach((filterKey) => {
    const value = filters[filterKey];

    if (isEmpty(value)) {
      // lodash isEmpty returns true for numbers
      if (typeof value !== 'number') {
        return;
      }
    }

    if (filterKey === 'price_filter') {
      if (!isNaN(value.min) && !isNaN(value.max)) {
        searchParams.append(filterKey, `${value.min}-${value.max}`);
      }
      return;
    }

    const val =
      typeof value === 'number' || typeof value === 'string'
        ? encodeURIComponent(value)
        : value.join('_');

    searchParams.append(filterKey, val);
  });

  searchParams.append('currentPage', String(page));

  searchParams.append('filter_format', 'v2');

  if (onlyFilters) {
    searchParams.append('activeFilter', '1');
  }

  if (fetchMeta) {
    searchParams.append('meta', '1');
  }

  searchParams.append('currency', DEFAULT_CURRENCY_CODE);
  searchParams.append('country_code', DEFAULT_COUNTRY);



  searchParams.append('deviceType', 'MSITE');

  if (catalogTagFilter) {
    searchParams.append('catalog_tag_filter', catalogTagFilter);
  }

  if (priceRevealEnabled) {
    searchParams.append('enable_pr', '1');
  }
  deviceOS = deviceOS || detectOS(headers?.['user-agent'] || '');
  searchParams.append(DEVICE_OS_KEY, DEVICE_OS_VALUE.MOBILE + '_' + deviceOS);
  const url = isOffer
    ? `${getUrl(ROUTES.LISTING_OFFER)}?${searchParams.toString()}`
    : `${getUrl(ROUTES.LISTING)}?${searchParams.toString()}`;

  return ApiHelper(url, { headers }).then(async (response: Response) => {
    try {
      if (response.ok === false) {
        return Promise.reject({ status: response.status, data: response });
      }
      return await response.json();
    } catch (e) {
      return Promise.reject({ status: 500 });
    }
  }).catch((err: { status?: number }) => {
    return Promise.reject(err);
  });
};

export const getStoreRedirectCount = async ({
  searchTerm,
  catalogTagFilter,
  headers = {},
}: {
  searchTerm: string;
  catalogTagFilter: string;
  headers?: IHeadersObject;
}): Promise<number> => {
  const searchParams = new URLSearchParams();
  searchParams.append('searchTerm', searchTerm);
  searchParams.append('es_agg_call_flag', 'False');
  searchParams.append('enable_redirection', 'False');
  searchParams.append('catalog_tag_filter', catalogTagFilter);

  const url = `${getUrl(ROUTES.LISTING)}?${searchParams.toString()}`;

  return ApiHelper(url, { headers }).then(async (response: Response) => {
    try {
      if (response.ok === false) {
        return Promise.reject({ status: response.status, data: response });
      }
      const data = await response.json();
      return data?.response?.count || 0;

    } catch (e) {
      return 0;
    }
  });
};
